import './KwelloButton.scss';
import { Button, ButtonProps } from '@progress/kendo-react-buttons';
import { KWELLO_ICON, KwelloSvgIcon } from '../icon/KwelloSvgIcon';
import { Tooltip } from 'react-tooltip';
import { uniqueString } from '../widgets/widget-utils/widgetUtils';

interface KwelloButtonProps extends ButtonProps {
  displayOnly?: boolean;
  icon?: KWELLO_ICON;
  endIcon?: KWELLO_ICON;
  iconColor?: 'primary' | 'secondary' | 'gray';
  tooltip?: string;
}

/**
 * `KwelloButton` is a custom button component that supports additional features such as icons,
 * tooltips, and display-only mode. It extends the standard button properties and adds specific
 * properties for enhanced functionality.
 *
 * @param props - The properties for the `KwelloButton` component.
 * @param props.className - Additional CSS classes to apply to the button.
 * @param props.displayOnly - If true, the button will be styled as display-only.
 * @param props.icon - The icon to display inside the button.
 * @param props.iconColor - The color of the icon.
 * @param props.rounded - The border radius of the button, defaults to 'full'.
 * @param props.tooltip - The text to display in the tooltip.
 * @param props.children - The content to display inside the button.
 *
 * @returns A JSX element representing the custom button with optional tooltip.
 */
function KwelloButton(props: Readonly<KwelloButtonProps>) {
  const { endIcon, className, displayOnly, icon, iconColor, tooltip, ...standardButtonProps } =
    props;
  const tooltipId = tooltip ? `button-tooltip-${uniqueString()}` : undefined;

  return (
    <>
      <Button
        className={`kwello-button ${displayOnly ? 'display-only' : ''} icon-color-${iconColor} ${className ?? ''}`}
        endIcon={endIcon && <KwelloSvgIcon icon={endIcon} />}
        startIcon={icon && <KwelloSvgIcon icon={icon} />}
        data-tooltip-id={tooltipId}
        size="medium"
        {...standardButtonProps}
      >
        {props.children}
      </Button>
      {tooltip && (
        <Tooltip id={tooltipId} place="top" style={{ fontSize: '0.8rem' }}>
          {tooltip}
        </Tooltip>
      )}
    </>
  );
}

export default KwelloButton;
